import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, CheckCircle2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useGooglePlacesAutocomplete,
  type ParsedAddress,
} from "@/hooks/useGooglePlacesAutocomplete";
import { getUiTranslations } from "@/lib/ui-translations";

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined;

interface AddressAutocompleteProps {
  onAddressSelected: (address: ParsedAddress) => void;
  onManualEntry: () => void;
  selectedAddress?: ParsedAddress | null;
  placeholder?: string;
  manualEntryLabel?: string;
  editLabel?: string;
  language?: string;
}

export function AddressAutocomplete({
  onAddressSelected,
  onManualEntry,
  selectedAddress,
  placeholder,
  manualEntryLabel,
  editLabel,
  language = 'fr',
}: AddressAutocompleteProps) {
  const t = getUiTranslations(language);
  const effectivePlaceholder = placeholder ?? t.searchAddress;
  const effectiveManualLabel = manualEntryLabel ?? t.manualAddressLink;
  const effectiveEditLabel = editLabel ?? t.editAddress;
  const { suggestions, search, selectPlace, clearSuggestions, isLoaded } =
    useGooglePlacesAutocomplete();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // If no API key, immediately show manual fields
  useEffect(() => {
    if (!API_KEY) onManualEntry();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-suggestion]");
    items[highlightIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setHighlightIndex(-1);
    if (value.trim()) {
      search(value);
      setOpen(true);
    } else {
      clearSuggestions();
      setOpen(false);
    }
  };

  const handleSelect = useCallback(
    async (placeId: string) => {
      setLoading(true);
      setOpen(false);
      const parsed = await selectPlace(placeId);
      if (parsed) {
        onAddressSelected(parsed);
        setQuery("");
        clearSuggestions();
      }
      setLoading(false);
    },
    [selectPlace, onAddressSelected, clearSuggestions]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex].placeId);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (!isLoaded) return null;

  // Address was selected via autocomplete — show confirmation
  if (selectedAddress) {
    const parts = [
      selectedAddress.street,
      selectedAddress.city,
      selectedAddress.province,
      selectedAddress.postalCode,
      selectedAddress.country,
    ].filter(Boolean);

    return (
      <div className="rounded-md border border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30 p-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">{parts.join(", ")}</p>
          </div>
          <button
            type="button"
            onClick={onManualEntry}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0"
          >
            <Pencil className="h-3 w-3" />
            {effectiveEditLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          className={cn(
            "flex h-12 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            loading && "opacity-60"
          )}
          disabled={loading}
          autoComplete="off"
        />
      </div>

      {open && suggestions.length > 0 && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg"
        >
          <div className="max-h-60 overflow-y-auto p-1">
            {suggestions.map((s, i) => (
              <button
                key={s.placeId}
                type="button"
                data-suggestion
                onClick={() => handleSelect(s.placeId)}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                  highlightIndex === i
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <MapPin className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{s.description}</span>
              </button>
            ))}
          </div>
          <div className="border-t px-2 py-1.5 text-[10px] text-muted-foreground text-right">
            Powered by Google
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onManualEntry}
        className="mt-2 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
      >
        {effectiveManualLabel}
      </button>
    </div>
  );
}
